import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { IonicModule, ModalController, PopoverController } from '@ionic/angular';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { CONVERSATION_PRIORITY_VALUES, DEFAULT_PRIORITY_TOKENS, SUPPORT_STEP_ASSIGNEE_TYPES } from 'src/app/constants';
import { createPriorityValueToTokenMap, isUrl } from 'src/app/utils/utils';
import { EscalationType, PriorityTokens } from 'src/models';
import { Entity } from 'src/models/entities';
import { ColonToEmojiPipe } from 'src/pipes/colonToEmoji.pipe';
import { EntitiesService } from 'src/services/entities.service';
import { EscalationTypesService } from 'src/services/escalation-types.service';
import { EscalationsService } from 'src/services/escalations.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';
import { TokenService } from 'src/services/token.service';

// Constants for escalation type selector values
const ESCALATION_TYPE_SELECTOR_VALUES = {
  DISABLED: 0,
  OPTIONAL: 1,
  REQUIRED: 2,
} as const;

@Component({
  selector: 'app-create-escalation',
  templateUrl: './create-escalation.component.html',
  styleUrls: ['./create-escalation.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, RouterModule, ColonToEmojiPipe],
})
export class CreateEscalationComponent {
  // Required inputs
  conversationId: string;
  projectId?: string;
  customerId?: string;

  // Form fields
  escalationTypeId: string;
  title: string;
  body: string;
  priority: number;
  assignedToId: string;
  assignedToType: string;
  assignedToName: string;

  // UI state
  isLoading = false;
  isLoadingEscalationTypes = false;
  escalationTypes: EscalationType[] = [];
  selectedEscalationType: EscalationType;
  priorityTokens: Record<string, PriorityTokens> = DEFAULT_PRIORITY_TOKENS;
  priorityValueToTokenMap: Record<number, PriorityTokens> = createPriorityValueToTokenMap(DEFAULT_PRIORITY_TOKENS);
  resolvedPriorityOptions: ActionDropdownType[] = [];
  assigneeEntities: { id: string; name: string; type: string; photo?: string }[] = [];

  // Constants
  readonly ESCALATION_TYPE_SELECTOR_VALUES = ESCALATION_TYPE_SELECTOR_VALUES;
  readonly CONVERSATION_PRIORITY_VALUES = CONVERSATION_PRIORITY_VALUES;

  // Helper function for template
  readonly isUrl = isUrl;

  constructor(
    private escalationsService: EscalationsService,
    private escalationTypesService: EscalationTypesService,
    private searchablePopoverService: SearchablePopoverService,
    private msgService: MessageService,
    private modalCtrl: ModalController,
    private tokenService: TokenService,
    private popoverCtrl: PopoverController,
    private entitiesService: EntitiesService,
  ) {}

  async ngOnInit() {
    await Promise.all([this.loadEscalationTypes(), this.initializePriorityTokens()]);
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  // Conditional visibility getters
  get showAssigneeField(): boolean {
    return this.selectedEscalationType?.assigneeSelector !== ESCALATION_TYPE_SELECTOR_VALUES.DISABLED;
  }

  get isAssigneeRequired(): boolean {
    return this.selectedEscalationType?.assigneeSelector === ESCALATION_TYPE_SELECTOR_VALUES.REQUIRED;
  }

  get showPriorityField(): boolean {
    return this.selectedEscalationType?.prioritySelector !== ESCALATION_TYPE_SELECTOR_VALUES.DISABLED;
  }

  get isPriorityRequired(): boolean {
    return this.selectedEscalationType?.prioritySelector === ESCALATION_TYPE_SELECTOR_VALUES.REQUIRED;
  }

  async loadEscalationTypes() {
    this.isLoadingEscalationTypes = true;
    try {
      const response = await this.escalationTypesService.list({
        where: [
          {
            field: 'projectId',
            operator: '==',
            value: this.projectId,
          },
        ],
      });
      this.escalationTypes = response.data as EscalationType[];

      if (this.escalationTypes.length === 1) {
        // If there is only one escalation type, select it by default
        this.selectedEscalationType = this.escalationTypes[0];
        this.escalationTypeId = this.escalationTypes[0].id;
        this.onEscalationTypeChange(this.escalationTypes[0]);
      }
    } catch (error) {
      this.msgService.showError(error);
    } finally {
      this.isLoadingEscalationTypes = false;
    }
  }

  async loadAssigneeEntities() {
    if (this.selectedEscalationType.assigneeSelectorIds?.length) {
      const result = await this.entitiesService.list({
        where: [{ field: 'id', operator: 'in', value: this.selectedEscalationType.assigneeSelectorIds }],
      });

      this.assigneeEntities = result.data.map((e: Entity) => ({
        id: e.id,
        name: e.name,
        type: e.type,
        photo: e.photo,
      }));

      // If there is only one assignee, select it by default
      if (this.assigneeEntities.length === 1) {
        this.assignedToId = this.assigneeEntities[0].id;
        this.assignedToName = this.assigneeEntities[0].name;
        this.assignedToType = this.assigneeEntities[0].type;
      }
    } else {
      this.assigneeEntities = [];
    }
  }
  async initializePriorityTokens() {
    try {
      this.priorityTokens = (
        await this.tokenService.getResolvedTokens(['PRIORITY_LABEL_MAP'], this.customerId, this.projectId)
      )['PRIORITY_LABEL_MAP'];
      this.priorityValueToTokenMap = createPriorityValueToTokenMap(this.priorityTokens);

      // Create resolved priority options for dropdown
      this.resolvedPriorityOptions = [
        {
          label: this.priorityTokens.LOW.LABEL,
          emoji: this.priorityTokens.LOW.EMOJI,
          metadata: { priority: CONVERSATION_PRIORITY_VALUES.LOW },
        },
        {
          label: this.priorityTokens.MEDIUM.LABEL,
          emoji: this.priorityTokens.MEDIUM.EMOJI,
          metadata: { priority: CONVERSATION_PRIORITY_VALUES.MEDIUM },
        },
        {
          label: this.priorityTokens.HIGH.LABEL,
          emoji: this.priorityTokens.HIGH.EMOJI,
          metadata: { priority: CONVERSATION_PRIORITY_VALUES.HIGH },
        },
        {
          label: this.priorityTokens.CRITICAL.LABEL,
          emoji: this.priorityTokens.CRITICAL.EMOJI,
          metadata: { priority: CONVERSATION_PRIORITY_VALUES.CRITICAL },
        },
      ];
    } catch (error) {
      console.error('Failed to load priority tokens:', error);
      // Fallback to default values if token service fails
      this.priorityTokens = DEFAULT_PRIORITY_TOKENS;
      this.priorityValueToTokenMap = createPriorityValueToTokenMap(DEFAULT_PRIORITY_TOKENS);

      // Create fallback priority options
      this.resolvedPriorityOptions = [
        {
          label: this.priorityTokens.LOW.LABEL,
          emoji: this.priorityTokens.LOW.EMOJI,
          metadata: { priority: CONVERSATION_PRIORITY_VALUES.LOW },
        },
        {
          label: this.priorityTokens.MEDIUM.LABEL,
          emoji: this.priorityTokens.MEDIUM.EMOJI,
          metadata: { priority: CONVERSATION_PRIORITY_VALUES.MEDIUM },
        },
        {
          label: this.priorityTokens.HIGH.LABEL,
          emoji: this.priorityTokens.HIGH.EMOJI,
          metadata: { priority: CONVERSATION_PRIORITY_VALUES.HIGH },
        },
        {
          label: this.priorityTokens.CRITICAL.LABEL,
          emoji: this.priorityTokens.CRITICAL.EMOJI,
          metadata: { priority: CONVERSATION_PRIORITY_VALUES.CRITICAL },
        },
      ];
    }
  }

  async showEscalationTypeOptions(event?: Event) {
    this.searchablePopoverService.showPopover({
      event,
      selectedItemId: this.escalationTypeId,
      loadData: async (searchText: string) => {
        return this.escalationTypes
          .filter((type) => (searchText ? type.name.toLowerCase().includes(searchText.toLowerCase()) : true))
          .map((type) => ({
            id: type.id,
            title: type.name,
            object: type,
          }));
      },
      callback: (id: string, name: string, object: any) => {
        this.onEscalationTypeChange(object);
      },
    });
  }

  async onEscalationTypeChange(escalationType: EscalationType) {
    this.selectedEscalationType = escalationType;
    this.escalationTypeId = escalationType.id;

    this.assignedToId = undefined;
    this.assignedToType = undefined;
    this.assignedToName = undefined;

    this.priority = undefined;

    await this.loadAssigneeEntities();
  }

  async showAssigneeOptions(event?: Event) {
    if (this.assigneeEntities?.length) {
      // Transform assigneeEntities to ActionDropdownType format
      const options: ActionDropdownType[] = this.assigneeEntities.map((entity) => ({
        label: entity.name,
        id: entity.id,
        emoji: entity.photo, // Use photo as emoji if it's a URL
        metadata: {
          type: entity.type,
          originalEntity: entity, // Store original entity for easy access
        },
      }));

      const popover = await this.popoverCtrl.create({
        component: ActionDropdownComponent,
        componentProps: {
          options,
          callback: async (idx: number, label: string, id?: string, metadata?: any) => {
            popover.dismiss();

            // Use the metadata to get the original entity data
            const selectedEntity = metadata?.originalEntity || this.assigneeEntities[idx];
            if (selectedEntity?.id) {
              this.assignedToId = selectedEntity.id;
              this.assignedToName = selectedEntity.name;
              this.assignedToType = selectedEntity.type;
            }
          },
        },
        showBackdrop: false,
        event,
      });
      popover.present();
    } else {
      // If allowed assignees are not explicitly set, just show all entities
      this.searchablePopoverService.entities({
        event,
        callback: async (id: string, name: string, object: any) => {
          this.assignedToId = id;
          this.assignedToName = name;
          this.assignedToType = object.type;
        },
      });
    }
  }

  async showPriorityOptions(event?: Event) {
    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options: this.resolvedPriorityOptions,
        callback: async (idx: number) => {
          popover.dismiss();

          const selectedOption = this.resolvedPriorityOptions[idx];
          if (selectedOption?.metadata?.priority) {
            this.priority = selectedOption.metadata.priority;
          }
        },
      },
      showBackdrop: false,
      event,
    });
    popover.present();
  }

  displayPriority(): string {
    if (!this.priority || !this.priorityValueToTokenMap) return 'None';

    const tokens = this.priorityValueToTokenMap[this.priority];
    if (!tokens) return 'None';

    return `${tokens.EMOJI} ${tokens.LABEL}`;
  }

  getPriorityEmoji(): string | null {
    if (!this.priority || !this.priorityValueToTokenMap) return null;
    const tokens = this.priorityValueToTokenMap[this.priority];
    return tokens?.EMOJI || null;
  }

  getPriorityLabel(): string | null {
    if (!this.priority || !this.priorityValueToTokenMap) return null;
    const tokens = this.priorityValueToTokenMap[this.priority];
    return tokens?.LABEL || null;
  }

  canSubmit(): boolean {
    // Always require escalation type and title
    if (!this.escalationTypeId || !this.title?.trim() || this.escalationTypes.length === 0) {
      return false;
    }

    // Check required assignee field
    if (this.isAssigneeRequired && !this.assignedToId) {
      return false;
    }

    // Check required priority field
    if (this.isPriorityRequired && !this.priority) {
      return false;
    }

    return true;
  }

  async submit() {
    if (!this.canSubmit()) {
      await this.msgService.show('Please fill in all required fields.');
      return;
    }

    this.isLoading = true;

    try {
      const escalation = await this.escalationsService.create({
        escalationTypeId: this.escalationTypeId,
        conversationId: this.conversationId,
        title: this.title,
        body: this.body,
        assignedToGroupId: this.assignedToType !== SUPPORT_STEP_ASSIGNEE_TYPES.USER ? this.assignedToId : undefined,
        assignedToUserId: this.assignedToType === SUPPORT_STEP_ASSIGNEE_TYPES.USER ? this.assignedToId : undefined,
        priority: this.priority,
      });

      this.isLoading = false;
      this.modalCtrl.dismiss({ escalation });
    } catch (e) {
      this.msgService.showError(e);
      this.isLoading = false;
    }
  }
}
